import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Printer } from 'lucide-react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { ModeBadge, Montant } from '../components/Montant';
import { aujourdhui, badgeStatut, chf, dateCourte, LIBELLE_MOYEN, LIBELLE_STATUT, moisLabel, moisPrecedent, soldeClass } from '../utils/format';
import type { Facture, Mode, MoisLedger, Paiement, ParticipantDetail, SeanceLedger } from '../types';

// La fiche d'une personne : son grand livre mois par mois (l'Excel, recalculé
// depuis les présences Éveil), et à droite ce qu'on fait avec : encaisser,
// facturer, régler le mode, le tarif et l'ouverture.
export function ParticipantPage({ id }: { id: number }) {
  const [d, setD] = useState<ParticipantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ouverts, setOuverts] = useState<Set<string>>(new Set());
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);

  const charger = useCallback(async () => {
    try {
      const data = await api.getParticipant(id);
      setD(data);
      setError(null);
      setOuverts((prev) => (prev.size ? prev : new Set(data.mois.slice(0, 2).map((m) => m.mois))));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec du chargement');
    }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);

  const agir = async (fn: () => Promise<unknown>) => {
    try { await fn(); await charger(); } catch (e) { setError(e instanceof Error ? e.message : 'Échec'); }
  };

  if (error && !d) return <div className="page"><div className="badge badge-err" style={{ display: 'block' }}>{error}</div></div>;
  if (!d) return <div className="page"><div className="spinner" /></div>;

  const basculer = (m: string) => setOuverts((prev) => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">{d.prenom} {d.nom}</span>
          <p className="page-meta flex flex-wrap items-center gap-8">
            <ModeBadge mode={d.mode} confirme={d.mode_confirme} />
            <span className={`badge ${d.actif ? 'badge-ok' : ''}`}>{d.actif ? 'Contrat en cours' : 'Contrat terminé'}</span>
            <span>{d.bailleur ?? '—'}{d.contrat ? ` · ${d.contrat}` : ''}</span>
            <span>{dateCourte(d.mandat_dernier_debut ?? d.mandat_debut)} → {dateCourte(d.mandat_fin)}</span>
            <span>Référent·e : {d.referent ?? '—'}</span>
            {d.email && <span>{d.email}</span>}
            {d.telephone && <span>{d.telephone}</span>}
          </p>
        </div>
        <div className="flex gap-8">
          <a className="btn btn-export" href={api.lienExportLedger(d.id)}><Download className="icon-sm" /> Exporter (CSV)</a>
          <button className="btn" onClick={() => naviguer('participants')}>Retour à la liste</button>
        </div>
      </div>

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      <div className="stats-grid mb-24">
        <div className="stat-card">
          <div className={`stat-value ${soldeClass(d.solde)}`}>{chf(d.solde)}</div>
          <div className="stat-label">{d.solde < 0 ? 'Solde — la personne doit' : d.solde > 0 ? 'Solde — avoir en sa faveur' : 'Solde — à jour'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value montant">{chf(d.total_du)}</div>
          <div className="stat-label">Dû depuis le {dateCourte(d.date_ouverture)} · {d.nb_seances} séance{d.nb_seances > 1 ? 's' : ''} × {chf(d.tarif_seance)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value montant">{chf(d.total_paye)}</div>
          <div className="stat-label">Payé depuis le {dateCourte(d.date_ouverture)} · dernier {dateCourte(d.dernier_paiement)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{d.seances_mois_courant}</div>
          <div className="stat-label">Séances suivies ce mois · {chf(d.seances_mois_courant * d.tarif_seance)}</div>
        </div>
      </div>

      <div className="fiche-grid">
        <div>
          <h3 className="section-label mb-12">Grand livre</h3>
          <div className="ledger">
            {d.mois.map((m) => (
              <MoisCard key={m.mois} m={m} ouvert={ouverts.has(m.mois)} onToggle={() => basculer(m.mois)}
                onExclure={(s) => { const motif = prompt(`Ne pas facturer la séance « ${s.atelier} » du ${dateCourte(s.date)}. Motif ?`, 'Gratuité convenue'); if (motif !== null) agir(() => api.addExclusion(d.id, s.attendance_id, motif)); }}
                onRetablir={(s) => agir(() => api.removeExclusion(d.id, s.attendance_id))}
                onRetirerPaiement={(p) => { if (confirm(`Retirer le paiement de ${chf(p.montant)} du ${dateCourte(p.date)} ?`)) agir(() => api.deletePaiement(p.id)); }}
              />
            ))}
            <div className="ledger-ouverture">
              Solde d'ouverture au {dateCourte(d.date_ouverture)} : <span className={soldeClass(d.solde_ouverture)}>{chf(d.solde_ouverture)}</span>
              {d.excel_feuille && <> — repris de l'Excel (feuille « {d.excel_feuille} », dernier solde {chf(d.excel_dernier_solde ?? 0)} au {dateCourte(d.excel_derniere_date)}).</>}
              {d.note && <><br />Note : {d.note}</>}
            </div>
            {d.historique_excel.length > 0 && (
              <div className="ledger-mois">
                <div className="ledger-mois-head" onClick={() => setHistoriqueOuvert((o) => !o)} style={{ gridTemplateColumns: '1fr auto' }}>
                  <span className="mois-nom" style={{ textTransform: 'none' }}>{historiqueOuvert ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />} Historique Excel — {d.historique_excel.length} paiement{d.historique_excel.length > 1 ? 's' : ''} avant l'ouverture</span>
                  <span><span className="k">total</span>{chf(d.historique_excel.reduce((a, p) => a + p.montant, 0))}</span>
                </div>
                {historiqueOuvert && (
                  <table className="table table-compact">
                    <tbody>
                      {d.historique_excel.map((p) => (
                        <tr key={p.id}><td className="tabular-nums">{dateCourte(p.date)}</td><td>{LIBELLE_MOYEN[p.moyen]}{p.note ? ` · ${p.note}` : ''}</td><td className="num"><Montant valeur={p.montant} /></td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-16">
          <PaiementForm onSubmit={(data) => agir(() => api.addPaiement(d.id, data))} />
          <FacturesPanel d={d} onCreer={(data) => agir(() => api.createFacture(d.id, data))} onChanger={(f, patch) => agir(() => api.updateFacture(f.id, patch))} />
          <ReglagesForm d={d} onSubmit={(patch) => agir(() => api.updateReglages(d.id, patch))} />
          <div className="panel">
            <div className="panel-header"><h3>Mandats (Éveil)</h3></div>
            <div className="panel-body">
              <table className="table table-compact">
                <tbody>
                  {d.mandats.map((m) => (
                    <tr key={m.id}>
                      <td className="text-sm">{m.mesure}{m.sisp ? '' : <span className="badge ml-4">hors SISP</span>}<span className="cell-sub">{m.bailleur}{m.contrat ? ` · ${m.contrat}` : ''}</span></td>
                      <td className="text-sm tabular-nums">{dateCourte(m.debut)} → {dateCourte(m.fin ?? m.fin_contractuelle)}{m.fin && m.fin_contractuelle && m.fin < m.fin_contractuelle ? <span className="cell-sub">fin anticipée (prévue {dateCourte(m.fin_contractuelle)})</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Un mois du grand livre ──
function MoisCard({ m, ouvert, onToggle, onExclure, onRetablir, onRetirerPaiement }: {
  m: MoisLedger; ouvert: boolean; onToggle: () => void;
  onExclure: (s: SeanceLedger) => void; onRetablir: (s: SeanceLedger) => void; onRetirerPaiement: (p: Paiement) => void;
}) {
  const lignes = useMemo(() => {
    const rows: { date: string; key: string; seance?: SeanceLedger; paiement?: Paiement }[] = [
      ...m.seances.map((s) => ({ date: s.date, key: `s${s.attendance_id}`, seance: s })),
      ...m.paiements.map((p) => ({ date: p.date, key: `p${p.id}`, paiement: p })),
    ];
    return rows.sort((a, b) => a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
  }, [m]);

  return (
    <div className="ledger-mois">
      <div className="ledger-mois-head" onClick={onToggle}>
        <span className="mois-nom">{ouvert ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />} {moisLabel(m.mois)}</span>
        <span><span className="k">séances</span>{m.nb_seances}</span>
        <span><span className="k">dû</span>{chf(m.total_du)}</span>
        <span><span className="k">payé</span>{chf(m.total_paye)}</span>
        <span><span className="k">solde fin</span><span className={soldeClass(m.solde_fin)}>{chf(m.solde_fin)}</span></span>
      </div>
      {ouvert && (
        lignes.length === 0 ? <div className="p-12 text-sm text-ink-3">Aucune séance ni paiement ce mois.</div> : (
          <table className="table table-compact">
            <thead><tr><th style={{ width: 96 }}>Date</th><th>Libellé</th><th className="num">Séance</th><th className="num">Coût</th><th className="num">Paiement</th><th style={{ width: 90 }}></th></tr></thead>
            <tbody>
              {lignes.map((l) => l.seance ? (
                <tr key={l.key} className={l.seance.exclue ? 'ledger-row-exclue' : ''}>
                  <td className="tabular-nums">{dateCourte(l.seance.date)}</td>
                  <td>
                    {l.seance.atelier}
                    {l.seance.facture_numero && <span className="badge ml-8" title="Déjà sur une facture">{l.seance.facture_numero}</span>}
                    {l.seance.exclue && <span className="cell-sub motif">non facturée : {l.seance.motif_exclusion ?? 'sans motif'}</span>}
                  </td>
                  <td className="num">{l.seance.exclue ? 0 : 1}</td>
                  <td className="num">{l.seance.exclue ? '—' : chf(l.seance.tarif)}</td>
                  <td className="num"></td>
                  <td className="num">
                    {l.seance.exclue
                      ? <button className="btn-ligne" onClick={() => onRetablir(l.seance!)}>rétablir</button>
                      : !l.seance.facture_id && <button className="btn-ligne" onClick={() => onExclure(l.seance!)}>exclure</button>}
                  </td>
                </tr>
              ) : (
                <tr key={l.key} className="ledger-row-paiement">
                  <td className="tabular-nums">{dateCourte(l.paiement!.date)}</td>
                  <td>Paiement {LIBELLE_MOYEN[l.paiement!.moyen].toLowerCase()}{l.paiement!.note ? ` — ${l.paiement!.note}` : ''}<span className="cell-sub">{l.paiement!.source === 'excel' ? 'repris de l’Excel' : `saisi par ${l.paiement!.cree_par ?? '—'}`}</span></td>
                  <td className="num"></td>
                  <td className="num"></td>
                  <td className="num"><Montant valeur={l.paiement!.montant} /></td>
                  <td className="num">{l.paiement!.source === 'app' && !l.paiement!.facture_id && <button className="btn-ligne" onClick={() => onRetirerPaiement(l.paiement!)}>retirer</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

// ── Encaisser ──
function PaiementForm({ onSubmit }: { onSubmit: (data: { date: string; montant: number; moyen: string; note: string }) => Promise<void> | void }) {
  const [date, setDate] = useState(aujourdhui());
  const [montant, setMontant] = useState('');
  const [moyen, setMoyen] = useState('cash');
  const [note, setNote] = useState('');
  const valide = Number(montant.replace(',', '.')) > 0 && !!date;
  return (
    <div className="panel">
      <div className="panel-header"><h3>Enregistrer un paiement</h3></div>
      <div className="panel-body">
        <form onSubmit={async (e) => { e.preventDefault(); if (!valide) return; await onSubmit({ date, montant: Number(montant.replace(',', '.')), moyen, note }); setMontant(''); setNote(''); }}>
          <div className="form-group flex gap-8">
            <div className="flex-1"><label className="form-label">Date</label><input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="flex-1"><label className="form-label">Montant (CHF)</label><input className="form-input" inputMode="decimal" placeholder="0.00" value={montant} onChange={(e) => setMontant(e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Moyen</label>
            <div className="selectable-group">
              {(['cash', 'virement', 'autre'] as const).map((mo) => (
                <button type="button" key={mo} className={`selectable ${moyen === mo ? 'active' : ''}`} onClick={() => setMoyen(mo)}>{LIBELLE_MOYEN[mo]}</button>
              ))}
            </div>
            <div className="text-xs text-ink-3 mt-4">Une facture payée se marque « Payée » dans la liste des factures : le paiement se crée tout seul.</div>
          </div>
          <div className="form-group"><label className="form-label">Note</label><input className="form-input" placeholder="ex. remis en main propre le mardi" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <button className="btn btn-accent" type="submit" disabled={!valide}>Enregistrer le paiement</button>
        </form>
      </div>
    </div>
  );
}

// ── Facturer ──
function FacturesPanel({ d, onCreer, onChanger }: {
  d: ParticipantDetail;
  onCreer: (data: { du: string; au: string; note: string }) => Promise<void> | void;
  onChanger: (f: Facture, patch: { statut?: string; payee_le?: string | null }) => Promise<void> | void;
}) {
  const prec = moisPrecedent();
  const [du, setDu] = useState(prec.du);
  const [au, setAu] = useState(prec.au);
  const [note, setNote] = useState('');
  return (
    <div className="panel">
      <div className="panel-header"><h3>Factures / relevés</h3></div>
      <div className="panel-body">
        <form onSubmit={async (e) => { e.preventDefault(); await onCreer({ du, au, note }); setNote(''); }}>
          <div className="form-group flex gap-8">
            <div className="flex-1"><label className="form-label">Du</label><input type="date" className="form-input" value={du} onChange={(e) => setDu(e.target.value)} /></div>
            <div className="flex-1"><label className="form-label">Au</label><input type="date" className="form-input" value={au} onChange={(e) => setAu(e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Note sur le relevé</label><input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <button className={`btn ${d.mode === 'facture' ? 'btn-accent' : ''}`} type="submit">Créer le relevé de la période</button>
          <div className="text-xs text-ink-3 mt-4">Reprend les séances de la période pas encore facturées, au tarif du moment.</div>
        </form>
        {d.factures.length > 0 && (
          <div className="mt-16">
            {d.factures.map((f) => (
              <div className="facture-item" key={f.id}>
                <div>
                  <span className="text-mono">{f.numero}</span> · {dateCourte(f.periode_du)} → {dateCourte(f.periode_au)} · {f.lignes.length} séance{f.lignes.length > 1 ? 's' : ''}
                  <span className="cell-sub"><span className={badgeStatut(f.statut)}>{LIBELLE_STATUT[f.statut]}</span>{f.emise_le ? ` · émise ${dateCourte(f.emise_le)}` : ''}{f.payee_le ? ` · payée ${dateCourte(f.payee_le)}` : ''}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Montant valeur={f.montant} />
                  {f.statut === 'brouillon' && <button className="btn btn-sm" onClick={() => onChanger(f, { statut: 'envoyee' })}>Envoyée</button>}
                  {f.statut === 'envoyee' && <button className="btn btn-sm btn-accent" onClick={() => onChanger(f, { statut: 'payee', payee_le: aujourdhui() })}>Payée</button>}
                  {(f.statut === 'brouillon' || f.statut === 'envoyee') && <button className="btn btn-sm btn-ghost" onClick={() => { if (confirm(`Annuler ${f.numero} ?`)) onChanger(f, { statut: 'annulee' }); }}>Annuler</button>}
                  <button className="btn btn-sm btn-ghost" title="Imprimer / PDF" onClick={() => naviguer('factures', f.id, 'imprimer')}><Printer className="icon-sm" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Régler ──
function ReglagesForm({ d, onSubmit }: { d: ParticipantDetail; onSubmit: (patch: { mode: Mode; mode_confirme: boolean; tarif_seance: number; solde_ouverture: number; date_ouverture: string; note: string | null }) => Promise<void> | void }) {
  const [mode, setMode] = useState<Mode>(d.mode);
  const [confirme, setConfirme] = useState(d.mode_confirme);
  const [tarif, setTarif] = useState(String(d.tarif_seance));
  const [solde, setSolde] = useState(String(d.solde_ouverture));
  const [date, setDate] = useState(d.date_ouverture);
  const [note, setNote] = useState(d.note ?? '');
  useEffect(() => { setMode(d.mode); setConfirme(d.mode_confirme); setTarif(String(d.tarif_seance)); setSolde(String(d.solde_ouverture)); setDate(d.date_ouverture); setNote(d.note ?? ''); }, [d]);
  const num = (s: string) => Number(s.replace(',', '.'));
  const valide = Number.isFinite(num(tarif)) && num(tarif) >= 0 && Number.isFinite(num(solde)) && /^\d{4}-\d{2}-\d{2}$/.test(date);
  return (
    <div className="panel">
      <div className="panel-header"><h3>Réglages</h3></div>
      <div className="panel-body">
        <form onSubmit={(e) => { e.preventDefault(); if (valide) onSubmit({ mode, mode_confirme: confirme, tarif_seance: num(tarif), solde_ouverture: num(solde), date_ouverture: date, note: note.trim() || null }); }}>
          <div className="form-group">
            <label className="form-label">Mode de paiement</label>
            <div className="selectable-group">
              <button type="button" className={`selectable ${mode === 'cash' ? 'active' : ''}`} onClick={() => setMode('cash')}>Cash</button>
              <button type="button" className={`selectable ${mode === 'facture' ? 'active' : ''}`} onClick={() => setMode('facture')}>Facture</button>
            </div>
            <label className="flex items-center gap-8 text-sm mt-8" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} /> Mode confirmé avec la personne
            </label>
          </div>
          <div className="form-group flex gap-8">
            <div className="flex-1"><label className="form-label">Tarif par séance (CHF)</label><input className="form-input" inputMode="decimal" value={tarif} onChange={(e) => setTarif(e.target.value)} /></div>
          </div>
          <div className="form-group flex gap-8">
            <div className="flex-1"><label className="form-label">Solde d'ouverture (CHF)</label><input className="form-input" inputMode="decimal" value={solde} onChange={(e) => setSolde(e.target.value)} /></div>
            <div className="flex-1"><label className="form-label">Date d'ouverture</label><input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          </div>
          <div className="text-xs text-ink-3 mb-12">Négatif = la personne doit. Les séances comptent à partir de la date d'ouverture ; avant, c'est l'historique Excel.</div>
          <div className="form-group"><label className="form-label">Note</label><textarea className="form-textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <button className="btn btn-accent" type="submit" disabled={!valide}>Enregistrer les réglages</button>
        </form>
      </div>
    </div>
  );
}
